import React, { useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { setMapModes } from '@actions/map-actions';
import { SwitchLabel } from '@shared/components/switch-label';
import { getMapModesSet } from '@selectors/map-selector';
import filterStyles from './filter.scss';
import styles from './overlaps-filter.scss';

const OverlapsFilter = ({ disabled, label }) => {
  const dispatch = useDispatch();
  const modes = useSelector(getMapModesSet);
  const agency = useSelector(state => state.dataTypes.agency);
  const agencyId = useSelector(state => state.auth.user.agencyId);
  const userAgency = agency[agencyId];

  const updateMode = useCallback((mode, enabled) => {
    // Sets the mode. We are now treating it as a radio, so we discard any previously set modes every time.
    const newModes = new Set();
    if (enabled) {
      newModes.add(mode);
    }
    dispatch(setMapModes(Array.from(newModes)));
  }, [dispatch]);

  const toggleConflict = useCallback((event, checked) => updateMode('conflict', checked), [updateMode]);
  const toggleOpportunity = useCallback((event, checked) => updateMode('opportunity', checked), [updateMode]);
  const togglePending = useCallback((event, checked) => updateMode('pending', checked), [updateMode]);
  const toggleResolved = useCallback((event, checked) => updateMode('resolved', checked), [updateMode]);

  let agencyName = '';
  if (userAgency && !userAgency.is_admin) {
    agencyName = `${userAgency.name} `;
  }

  return (
    <fieldset className={filterStyles['content-filter-group']}>
      <legend className={filterStyles.title}>
        <div className={filterStyles.label}>
          Highlight {agencyName}{label.toLowerCase()} containing:
        </div>
      </legend>
      <div className={styles['overlap-row']}>
        <SwitchLabel
          disabled={disabled}
          toggled={!disabled && modes.has('conflict')}
          onToggle={toggleConflict}
          label="Open conflicts"
          icon={<img src="https://storage.googleapis.com/coordinate-assets/icon/conflict.svg" alt=""/>}
        />
        <SwitchLabel
          disabled={disabled}
          toggled={!disabled && modes.has('pending')}
          onToggle={togglePending}
          label="Pending conflicts"
          icon={<img src="https://storage.googleapis.com/coordinate-assets/icon/pending.svg" alt=""/>}
        />
        <SwitchLabel
          disabled={disabled}
          toggled={!disabled && modes.has('resolved')}
          onToggle={toggleResolved}
          label="Resolved conflicts"
          icon={<img src="https://storage.googleapis.com/coordinate-assets/icon/resolved.svg" alt=""/>}
        />
        <SwitchLabel
          disabled={disabled}
          toggled={!disabled && modes.has('opportunity')}
          onToggle={toggleOpportunity}
          label="Opportunities"
          icon={<img src="https://storage.googleapis.com/coordinate-assets/icon/opportunity.svg" alt=""/>}
        />
      </div>
    </fieldset>
  );
};

OverlapsFilter.propTypes = {
  disabled: PropTypes.bool,
  label: PropTypes.string
};

export default memo(OverlapsFilter);
